import React, { type ReactNode } from "react";
import { IntercomProvider } from "react-use-intercom";
import { useAppRouter } from "lib/useAppRouter";

import { config } from "lib/config";
import { Analytics } from "lib/analytics/analytics";

// Intercom contributes with more than 300KB of JS, so we delay its initialization to avoid affecting the LCP.
// https://github.com/devrnt/react-use-intercom?tab=readme-ov-file#delay-initialization
const INTERCOM_CONFIG = {
  DEFAULT_DELAY: 11000,
  QUERY_PARAM: "skip_cs_support_delay",
} as const;

// Intercom initialization delay can be skipped to have it loaded immediately
// by adding ?skip_cs_support_delay=true to the URL
const getIntercomDelay = (query: Record<string, string | string[] | undefined>) => {
  return query[INTERCOM_CONFIG.QUERY_PARAM] === "true" ? 0 : INTERCOM_CONFIG.DEFAULT_DELAY;
};

type PuzzleIntercomProviderProps = {
  children: ReactNode;
  shouldInitialize: boolean;
};

export const PuzzleIntercomProvider = ({
  children,
  shouldInitialize,
}: PuzzleIntercomProviderProps) => {
  const { router } = useAppRouter();
  const intercomInitializeDelay = getIntercomDelay(router.query);

  const handleShow = () => {
    Analytics.intercomOpened();
  };

  return (
    <IntercomProvider
      appId={config.INTERCOM_APP_ID!}
      onShow={handleShow}
      shouldInitialize={shouldInitialize}
      initializeDelay={intercomInitializeDelay}
    >
      {children}
    </IntercomProvider>
  );
};
